import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  Box,
  Drawer,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterLuxon } from '@mui/x-date-pickers-pro/AdapterLuxon';
import { memo, MouseEvent, ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RESPONSIVE_DRAWER_HEIGHT } from '../../Constants';
import { SortDTO } from '../../api';
import { DeliveryDTO, DeliveryFilterDTO, UserPopulatedDTO } from '../../api/generated';
import { useDeliveryList } from '../../api/queries/delivery';
import { useDeliveryFilterOptions } from '../../api/queries/deliveryFilterOptions';
import i18n from '../../i18n';
import { useSyncState } from '../../utils';
import { DetailContainerLayout } from '../DetailView/DetailContainerLayout';
import { DetailTitlebar } from '../DetailView/DetailTitlebar';
import { ListIndexNavigation } from '../DetailView/ListIndexNavigation';
import { ListWalkNavigation } from '../DetailView/ListWalkNavigation';
import { ActiveFilterChips } from '../Filter/ActiveFilterChips';
import { CombinedFilterList } from '../Filter/CombinedFilterList';
import { FilterHeaderLayout } from '../Filter/FilterHeaderLayout';
import { FilterPanelFooter } from '../Filter/FilterPanelFooter';
import { FilterPanelHeader } from '../Filter/FilterPanelHeader';
import { FilterPanelBodyLayout, FilterPanelContainer } from '../Filter/FilterPanelLayout';
import { ResetFiltersButton } from '../Filter/ResetFiltersButton';
import { ShowFilterButton } from '../Filter/ShowFilterButton';
import { FilterSpec } from '../Filter/common';
import { MenuBar } from '../MenuBar';
import { useSnackbar } from '../SnackbarProvider';
import { DeliveriesTable, DeliverySorting } from './DeliveriesTable';
import { DeliveryDetails } from './DeliveryDetails';
import { exportMultipleDeliveriesCSV, exportMultipleDeliveriesPDF } from './deliveryExport';

const pageSize = 50;
const defaultSorting: SortDTO<DeliveryDTO> = {
  sortDirection: 'desc',
  sortField: 'date',
};

export const deliveryFilterSpec: FilterSpec[] = [
  { filterKey: 'date', type: 'DateRange', displayName: 'delivery.date' },
  { filterKey: 'has_truck_scan_img', type: 'BinaryChoice', displayName: 'delivery.has_truck_scan_img' },
  {
    filterKey: 'netto_weight',
    type: 'ScaledNumberRange',
    filterProps: { unitName: 't', factor: 1000 },
    displayName: 'delivery.netto_weight',
  },
  { filterKey: 'avv', type: 'StringSelect', displayName: 'delivery.avv' },
  { filterKey: 'material', type: 'StringSelect', displayName: 'delivery.material' },
  { filterKey: 'customer_id', type: 'StringSelect', displayName: 'delivery.customer_id' },
  { filterKey: 'numberplate', type: 'StringSelect', displayName: 'delivery.numberplate' },
  { filterKey: 'delivery_id', type: 'StringSelect', displayName: 'delivery.delivery_id' },
];

export interface DocumentationPageProps {
  user: UserPopulatedDTO;
}

export const DocumentationPage = memo(function ({ user }: DocumentationPageProps): ReactElement {
  const theme = useTheme();
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filter, setFilter] = useState<DeliveryFilterDTO>({});
  const [transientFilter, transientFilterSync, setTransientFilter] = useSyncState<DeliveryFilterDTO>({});
  const { data: filterOptions } = useDeliveryFilterOptions({
    jobsiteId: user.preferredJobsite?.id,
    filter,
  });

  const [deliveriesPage, setDeliveriesPage] = useState(0);
  const [deliveriesSorting, setDeliveriesSorting] = useState<SortDTO<DeliveryDTO>>(defaultSorting);

  const { data: deliveries } = useDeliveryList({
    jobsiteId: user.preferredJobsite?.id,
    filter,
    pageSize,
    pageNumber: deliveriesPage,
    sort: deliveriesSorting,
  });

  const [selectedDeliveryIdx, setSelectedDeliveryIdx] = useState<number | undefined>(undefined);
  const [selectedEntries, setSelectedEntries] = useState<string[]>([]);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  useEffect(() => {
    if (isMdUp) {
      setIsFilterDrawerOpen(false);
    }
  }, [isMdUp]);

  function handleResetFilters(): void {
    setFilter({});
  }

  function handleRowSelect(deliveryId: string | null): void {
    if (deliveries?.data) {
      const idx = deliveries.data.findIndex((v) => v.id === deliveryId);
      if (idx >= 0) {
        setSelectedDeliveryIdx(idx + deliveriesPage * pageSize);
        return;
      }
    }
    // handle problems by resetting selection
    setSelectedDeliveryIdx(undefined);
  }

  const selectedDeliveryId = useMemo(() => {
    if (selectedDeliveryIdx !== undefined && deliveries) {
      const indexOnPage = selectedDeliveryIdx - deliveries.offset;
      if (0 <= indexOnPage && indexOnPage < deliveries.data.length) {
        return deliveries.data[indexOnPage].id;
      }
    }
    return undefined;
  }, [selectedDeliveryIdx, deliveries]);

  useEffect(
    function movePageToCoverSelection() {
      if (deliveries && selectedDeliveryIdx !== undefined) {
        const selectionPage = Math.floor(selectedDeliveryIdx / pageSize);
        if (selectionPage !== deliveriesPage) {
          setDeliveriesPage(selectionPage);
        }
      }
    },
    [selectedDeliveryIdx, deliveriesPage, deliveries]
  );

  const detailDelivery = useMemo(() => {
    return selectedDeliveryId ? deliveries?.data.find((v) => v.id === selectedDeliveryId) : undefined;
  }, [selectedDeliveryId, deliveries]);

  function handleSortChange(sort: DeliverySorting | null): void {
    setDeliveriesSorting(sort ? { sortField: sort.field, sortDirection: sort.sort } : defaultSorting);
  }

  function handleCloseDetails(): void {
    setSelectedDeliveryIdx(undefined);
  }

  function handleFilterCommit(): void {
    setFilter(transientFilterSync.current);
  }

  useEffect(
    function syncTransientFilter(): void {
      setTransientFilter(filter);
    },
    [filter, setTransientFilter]
  );

  function handleOpenFilterDrawer(): void {
    setIsFilterDrawerOpen(true);
  }

  function handleCloseFilterDrawer(): void {
    setIsFilterDrawerOpen(false);
  }

  function handleSelectEntry(selectedIds: string[]): void {
    setSelectedEntries(selectedIds);
  }

  function handleOpenDownloadMenu(event: MouseEvent<HTMLButtonElement>): void {
    setDownloadMenuAnchor(event.currentTarget);
  }

  function handleCloseDownloadMenu(): void {
    setDownloadMenuAnchor(null);
  }

  function getSelectedDeliveryData(): DeliveryDTO[] {
    if (selectedEntries.length === 0 || !deliveries?.data) {
      return [];
    }
    const deliveriesMap = new Map(deliveries.data.map((d) => [d.id, d]));
    return selectedEntries.map((id) => deliveriesMap.get(id)).filter((d): d is DeliveryDTO => !!d);
  }

  async function handleDownloadPDF(): Promise<void> {
    handleCloseDownloadMenu();
    const selectedDeliveryData = getSelectedDeliveryData();

    if (selectedDeliveryData.length === 0) {
      return;
    }

    try {
      await exportMultipleDeliveriesPDF({
        deliveries: selectedDeliveryData,
        filename: `${t('delivery.delivery_note', { count: selectedDeliveryData.length })}-${new Date().toLocaleString()}`,
        capturer_id: user.preferredJobsite?.capturer_id || '',
      });
    } catch (error) {
      console.error('Error exporting PDFs:', error);
      showSnackbar('download.pdf.error', 'error');
    }
  }

  async function handleDownloadCSV(): Promise<void> {
    handleCloseDownloadMenu();
    const selectedDeliveryData = getSelectedDeliveryData();

    if (selectedDeliveryData.length === 0) {
      return;
    }

    try {
      await exportMultipleDeliveriesCSV({
        deliveries: selectedDeliveryData,
        filename: `${t('delivery.delivery_note', { count: selectedDeliveryData.length })}-${new Date().toLocaleString()}`,
        capturer_id: user.preferredJobsite?.capturer_id || '',
      });
      showSnackbar('download.csv.success', 'success', `${selectedDeliveryData.length}`);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      showSnackbar('download.csv.error', 'error');
    }
  }

  async function handleDownloadSinglePDF(): Promise<void> {
    if (!detailDelivery) {
      return;
    }

    try {
      await exportMultipleDeliveriesPDF({
        deliveries: [detailDelivery],
        filename: `${t('delivery.delivery_note')}-${detailDelivery.delivery_id}`,
        capturer_id: user.preferredJobsite?.capturer_id || '',
      });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      showSnackbar('download.pdf.error', 'error');
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={i18n.language}>
      <Box display={'flex'}>
        <MenuBar />
        <Box
          sx={{
            height: `calc(100% - ${RESPONSIVE_DRAWER_HEIGHT}px)`,
            width: '100%',
            top: RESPONSIVE_DRAWER_HEIGHT,
            position: 'absolute',
            boxSizing: 'border-box',
            padding: 2,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Grid container columnSpacing={2} sx={{ height: '100%', flexWrap: 'nowrap' }}>
            {isMdUp ? (
              // large screen: regular display
              <Grid size={{ md: 3, lg: 3 }}>
                <Paper elevation={8} sx={{ borderRadius: '10px', padding: 2, height: 'calc(100% - 2rem)' }}>
                  <FilterPanelContainer>
                    <FilterPanelHeader />
                    <FilterPanelBodyLayout>
                      <CombinedFilterList
                        filterSpec={deliveryFilterSpec}
                        filterOptions={filterOptions}
                        filter={transientFilter}
                        onFilterChange={setTransientFilter}
                        onCommit={handleFilterCommit}
                      />
                    </FilterPanelBodyLayout>
                  </FilterPanelContainer>
                </Paper>
              </Grid>
            ) : (
              // small screen: expandable drawer
              <Drawer
                anchor='left'
                open={isFilterDrawerOpen}
                onClose={handleCloseFilterDrawer}
                PaperProps={{
                  sx: {
                    width: {
                      xs: '90%',
                      sm: '60%',
                    },
                    top: {
                      xs: RESPONSIVE_DRAWER_HEIGHT - 8,
                      sm: RESPONSIVE_DRAWER_HEIGHT,
                    },
                    height: {
                      xs: `calc(100% - ${RESPONSIVE_DRAWER_HEIGHT}px + 8px)`,
                      sm: `calc(100% - ${RESPONSIVE_DRAWER_HEIGHT}px)`,
                    },
                  },
                }}
              >
                <FilterPanelContainer>
                  <FilterPanelHeader onClose={handleCloseFilterDrawer} />
                  <FilterPanelBodyLayout>
                    <CombinedFilterList
                      filterSpec={deliveryFilterSpec}
                      filterOptions={filterOptions}
                      filter={transientFilter}
                      onFilterChange={setTransientFilter}
                      onCommit={handleFilterCommit}
                    />
                  </FilterPanelBodyLayout>
                  <FilterPanelFooter onReset={handleResetFilters} itemCount={deliveries?.total} />
                </FilterPanelContainer>
              </Drawer>
            )}

            <Grid size={{ xs: 12, md: 9, lg: 9 }} sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              <FilterHeaderLayout>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {!isMdUp && <ShowFilterButton onClick={handleOpenFilterDrawer} />}
                    <ResetFiltersButton filter={filter} onFilterChange={setFilter} />
                    <ActiveFilterChips filterSpec={deliveryFilterSpec} filter={filter} onFilterChange={setFilter} />
                  </Box>
                  <Box sx={{ display: 'flex', alignContent: 'center', justifyItems: 'center', gap: 1 }}>
                    <IconButton
                      onClick={handleOpenDownloadMenu}
                      disabled={selectedEntries.length === 0}
                      aria-label={t('common.download')}
                      edge='start'
                    >
                      <DownloadIcon />
                    </IconButton>
                    <Menu
                      anchorEl={downloadMenuAnchor}
                      open={Boolean(downloadMenuAnchor)}
                      onClose={handleCloseDownloadMenu}
                      MenuListProps={{
                        'aria-labelledby': 'download-button',
                      }}
                    >
                      <MenuItem onClick={handleDownloadPDF}>
                        <ListItemIcon>
                          <PictureAsPdfIcon fontSize='small' />
                        </ListItemIcon>
                        <ListItemText>{t('download.pdf.button')}</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={handleDownloadCSV}>
                        <ListItemIcon>
                          <DescriptionIcon fontSize='small' />
                        </ListItemIcon>
                        <ListItemText>{t('download.csv.button')}</ListItemText>
                      </MenuItem>
                    </Menu>
                  </Box>
                </Box>
              </FilterHeaderLayout>
              <DeliveriesTable
                data={deliveries || null}
                selectedId={selectedDeliveryId}
                selectedEntries={selectedEntries}
                onSelect={handleRowSelect}
                onSelectEntry={handleSelectEntry}
                pageSize={pageSize}
                page={deliveriesPage}
                onPageChange={setDeliveriesPage}
                onSortChange={handleSortChange}
              />
            </Grid>
          </Grid>
        </Box>

        <Drawer
          open={selectedDeliveryIdx !== undefined}
          onClose={handleCloseDetails}
          anchor='right'
          ModalProps={{
            slotProps: {
              backdrop: {
                invisible: true,
              },
            },
          }}
          PaperProps={{
            sx: {
              height: '100dvh',
              width: { sm: '100%', md: '60%' },
              marginRight: '-1rem',
              paddingRight: '1rem',
              top: RESPONSIVE_DRAWER_HEIGHT,
              overflow: 'hidden',
            },
          }}
        >
          <DetailContainerLayout>
            <DetailTitlebar
              onClose={handleCloseDetails}
              title={detailDelivery ? `${t('delivery.delivery_id')}: ${detailDelivery.delivery_id}` : 'Details'}
              adornment={
                <IconButton sx={{ mt: 0.25 }} onClick={handleDownloadSinglePDF} aria-label={t('download.pdf.button')}>
                  <DownloadIcon />
                </IconButton>
              }
            >
              <ListIndexNavigation
                Component={ListWalkNavigation}
                maxIdx={deliveries?.total}
                currentIdx={selectedDeliveryIdx}
                onIndexChange={setSelectedDeliveryIdx}
              />
            </DetailTitlebar>
            {detailDelivery && (
              <DeliveryDetails
                key={detailDelivery.id}
                organisationId={user.organisation.id}
                isUserAdmin={user.admin}
                delivery={detailDelivery}
                capturer_id={user.preferredJobsite?.capturer_id}
              />
            )}
          </DetailContainerLayout>
        </Drawer>
      </Box>
    </LocalizationProvider>
  );
});
